import { useMemo, useState, type FC } from "react";
import { useNavigate } from "react-router-dom";

import { useUserProfiles } from "../../../userProfiles/context/UserProfilesContext";
import { SectionHeader } from "../../components/SectionHeader";
import { useClientProfile } from "../../context/ClientProfileContext";
import { NAV_ITEMS } from "../../navConfig";
import { ActivityFeed } from "./ActivityFeed";
import {
  ActivityHeader,
  RefreshActivityButton,
  SubscribeButton
} from "./ActivityHeader";
import {
  DEFAULT_ACTIVITY_FILTERS,
  activeActivityFilterCount,
  caseOptionsFrom,
  filterActivity,
  isRoutineActivity,
  type ActivityFilters
} from "./activityModel";
import { useActivityFeed } from "./useActivityFeed";
import { useActivitySubscription } from "./useActivitySubscription";

/** Section header (subscribe, refresh) and filters over the live activity feed for one client. */
export const ActivityTab: FC = () => {
  const { clientId, matters } = useClientProfile();
  const navigate = useNavigate();
  const { get: getProfile } = useUserProfiles();
  const [filters, setFilters] = useState<ActivityFilters>(
    DEFAULT_ACTIVITY_FILTERS
  );

  const feed = useActivityFeed(clientId, { filters });
  const subscription = useActivitySubscription(clientId);

  const cases = useMemo(
    () =>
      caseOptionsFrom(
        matters.matters.map((m) => ({
          id: m.individual_id,
          visa: (m.case?.visa as string | undefined) ?? null
        })),
        feed.events
      ),
    [feed.events, matters.matters]
  );

  const visible = useMemo(
    () => filterActivity(feed.events, filters, getProfile),
    [feed.events, filters, getProfile]
  );
  const hasFilters = activeActivityFilterCount(filters) > 0;
  const routineCount = useMemo(
    () => feed.events.filter(isRoutineActivity).length,
    [feed.events]
  );
  const onlyRoutine =
    !filters.routine && routineCount > 0 && routineCount === feed.events.length;

  return (
    <div className="flex h-full min-h-0 w-full flex-col">
      <SectionHeader
        title={NAV_ITEMS.activity.label}
        actions={
          <>
            <SubscribeButton
              subscribed={subscription.subscribed}
              subscriberCount={subscription.subscriberCount}
              isLoading={subscription.isLoading || subscription.isSaving}
              onToggle={() => void subscription.toggle()}
            />
            <RefreshActivityButton
              onRefresh={feed.reload}
              isRefreshing={feed.isLoading}
            />
          </>
        }
      />
      <ActivityHeader
        filters={filters}
        onChange={setFilters}
        cases={cases}
        routineCount={routineCount}
      />
      <div className="min-h-0 flex-1 px-3 pt-2">
        <ActivityFeed
          events={visible}
          getUserProfile={getProfile}
          navigate={navigate}
          isLoading={feed.isLoading}
          error={feed.error}
          onRetry={feed.reload}
          hasMore={feed.hasMore}
          isLoadingMore={feed.isLoadingMore}
          onLoadMore={feed.loadMore}
          liveIds={feed.liveIds}
          layout="timeline"
          emptyTitle={
            hasFilters
              ? "No activity matches these filters."
              : onlyRoutine
              ? "Only routine activity so far."
              : "No activity yet."
          }
          emptyDescription={
            hasFilters
              ? "Try adjusting your filters or date range."
              : onlyRoutine
              ? "Conversations started, links opened and downloads are hidden. Use Show Routine to see them."
              : "Changes to this client, their cases and documents will show up here."
          }
        />
      </div>
    </div>
  );
};
